import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../../services/apiResume";
import toast from "react-hot-toast";

// Education Hooks
export function useEducation() {
    const queryClient = useQueryClient();

    const { data: education, isLoading } = useQuery({
        queryKey: ["education"],
        queryFn: api.getEducation,
    });

    const { mutate: addEducation, isLoading: isAdding } = useMutation({
        mutationFn: api.createEducation,
        onSuccess: () => {
            toast.success("Education added successfully");
            queryClient.invalidateQueries({ queryKey: ["education"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: updateEducation, isLoading: isUpdating } = useMutation({
        mutationFn: api.updateEducation,
        onSuccess: () => {
            toast.success("Education updated successfully");
            queryClient.invalidateQueries({ queryKey: ["education"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: deleteEducation, isLoading: isDeleting } = useMutation({
        mutationFn: api.deleteEducation,
        onSuccess: () => {
            toast.success("Education deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["education"] });
        },
        onError: (err) => toast.error(err.message),
    });

    return { education, isLoading, addEducation, updateEducation, deleteEducation, isAdding, isUpdating, isDeleting };
}

// Experience Hooks
export function useExperience() {
    const queryClient = useQueryClient();

    const { data: experience, isLoading } = useQuery({
        queryKey: ["experience"],
        queryFn: api.getExperience,
    });

    const { mutate: addExperience, isLoading: isAdding } = useMutation({
        mutationFn: api.createExperience,
        onSuccess: () => {
            toast.success("Experience added successfully");
            queryClient.invalidateQueries({ queryKey: ["experience"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: updateExperience, isLoading: isUpdating } = useMutation({
        mutationFn: api.updateExperience,
        onSuccess: () => {
            toast.success("Experience updated successfully");
            queryClient.invalidateQueries({ queryKey: ["experience"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: deleteExperience, isLoading: isDeleting } = useMutation({
        mutationFn: api.deleteExperience,
        onSuccess: () => {
            toast.success("Experience deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["experience"] });
        },
        onError: (err) => toast.error(err.message),
    });

    return { experience, isLoading, addExperience, updateExperience, deleteExperience, isAdding, isUpdating, isDeleting };
}

// Skills Hooks
export function useSkills() {
    const queryClient = useQueryClient();

    const { data: skills, isLoading } = useQuery({
        queryKey: ["skills"],
        queryFn: api.getSkills,
    });

    const { mutate: addSkill, isLoading: isAdding } = useMutation({
        mutationFn: api.createSkill,
        onSuccess: () => {
            toast.success("Skill added successfully");
            queryClient.invalidateQueries({ queryKey: ["skills"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: updateSkill, isLoading: isUpdating } = useMutation({
        mutationFn: api.updateSkill,
        onSuccess: () => {
            toast.success("Skill updated successfully");
            queryClient.invalidateQueries({ queryKey: ["skills"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: deleteSkill, isLoading: isDeleting } = useMutation({
        mutationFn: api.deleteSkill,
        onSuccess: () => {
            toast.success("Skill deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["skills"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: deleteSkillsBulk, isLoading: isDeletingBulk } = useMutation({
        mutationFn: api.deleteSkillsBulk,
        onSuccess: () => {
            toast.success("Selected skills deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["skills"] });
        },
        onError: (err) => toast.error(err.message),
    });

    return { skills, isLoading, addSkill, updateSkill, deleteSkill, deleteSkillsBulk, isAdding, isUpdating, isDeleting, isDeletingBulk };
}

// Projects Hooks
export function useProjects() {
    const queryClient = useQueryClient();

    const { data: projects, isLoading } = useQuery({
        queryKey: ["projects"],
        queryFn: api.getProjects,
    });

    const { mutate: addProject, isLoading: isAdding } = useMutation({
        mutationFn: api.createProject,
        onSuccess: () => {
            toast.success("Project added successfully");
            queryClient.invalidateQueries({ queryKey: ["projects"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: updateProject, isLoading: isUpdating } = useMutation({
        mutationFn: api.updateProject,
        onSuccess: () => {
            toast.success("Project updated successfully");
            queryClient.invalidateQueries({ queryKey: ["projects"] });
        },
        onError: (err) => toast.error(err.message),
    });

    const { mutate: deleteProject, isLoading: isDeleting } = useMutation({
        mutationFn: api.deleteProject,
        onSuccess: () => {
            toast.success("Project deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["projects"] });
        },
        onError: (err) => toast.error(err.message),
    });

    return { projects, isLoading, addProject, updateProject, deleteProject, isAdding, isUpdating, isDeleting };
}
